// Patient-friendly explainer for an Rx row: turns dosage / when / frequency /
// duration ("0-0-1", "after food", "30 days") into one simple sentence in the
// prescription's language, e.g. "रात को 1 लें, खाने के बाद, 30 दिन तक।"
//
// Design constraints:
// - resilient: any field may be missing or free text — unknown values are either
//   included as-is (short values) or skipped; with nothing usable, '' is returned
//   and nothing renders
// - exactly ONE language is used (the first supported non-English preferred
//   prescription language, else English)
// - taper continuation rows get an "After that:" prefix in the same language

export const RX_EXPLAINER_SUPPORTED_LANGUAGES = ['en', 'hi', 'kn', 'te', 'ta']

const STRINGS = {
  en: {
    take: (doseList) => `Take ${doseList}`,
    takeBare: (doseText) => `Take ${doseText}`,
    slotPhrase: (qty, slot) => `${qty} ${slot}`,
    slots: { morning: 'in the morning', afternoon: 'in the afternoon', evening: 'in the evening', night: 'at night' },
    half: 'half',
    and: 'and',
    then: 'After that',
    taperAfter: (n, unit, isFirst) =>
      `After the ${isFirst ? 'first' : 'next'} ${n} ${n === 1 ? unit : `${unit}s`}`,
    fullStop: '.',
    duration: { day: (n) => `for ${n} ${n === 1 ? 'day' : 'days'}`, week: (n) => `for ${n} ${n === 1 ? 'week' : 'weeks'}`, month: (n) => `for ${n} ${n === 1 ? 'month' : 'months'}`, year: (n) => `for ${n} ${n === 1 ? 'year' : 'years'}` },
    phrases: {
      'after food': 'after food', 'after meals': 'after food', 'post meals': 'after food',
      'before food': 'before food', 'before meals': 'before food',
      'with food': 'with food', 'along with food': 'with food',
      'empty stomach': 'on an empty stomach', 'on empty stomach': 'on an empty stomach',
      'at bedtime': 'at bedtime', 'before sleeping': 'at bedtime', 'before bed': 'at bedtime', 'hs': 'at bedtime',
      'with milk': 'with milk', 'with plenty of water': 'with plenty of water', 'with lots of water': 'with plenty of water',
      'before breakfast': 'before breakfast', 'after breakfast': 'after breakfast',
      'after lunch': 'after lunch', 'after dinner': 'after dinner',
      'morning': 'in the morning', 'afternoon': 'in the afternoon', 'evening': 'in the evening', 'night': 'at night',
      'once daily': 'once a day', 'od': 'once a day', 'twice daily': 'twice a day', 'bd': 'twice a day', 'bid': 'twice a day',
      'thrice daily': 'three times a day', 'three times daily': 'three times a day', 'tds': 'three times a day', 'tid': 'three times a day',
      'four times daily': 'four times a day', 'qid': 'four times a day',
      'weekly': 'once a week', 'once a week': 'once a week', 'alternate days': 'every other day',
      'sos': 'only when needed', 'stat': 'immediately, one dose',
      'continue': 'continue until advised otherwise', 'till next visit': 'until the next visit', 'until next visit': 'until the next visit',
      'lifelong': 'lifelong',
    },
  },
  hi: {
    take: (doseList) => `${doseList} लें`,
    takeBare: (doseText) => `${doseText} लें`,
    slotPhrase: (qty, slot) => `${slot} ${qty}`,
    slots: { morning: 'सुबह', afternoon: 'दोपहर', evening: 'शाम', night: 'रात को' },
    half: 'आधा',
    and: 'और',
    units: {
      tablet: 'गोली', tablets: 'गोली', tab: 'गोली', tabs: 'गोली',
      capsule: 'कैप्सूल', capsules: 'कैप्सूल', cap: 'कैप्सूल', caps: 'कैप्सूल',
      drop: 'बूंद', drops: 'बूंदें', puff: 'पफ़', puffs: 'पफ़',
      teaspoon: 'छोटा चम्मच', tsp: 'छोटा चम्मच', tablespoon: 'बड़ा चम्मच', tbsp: 'बड़ा चम्मच',
      sachet: 'पाउच', scoop: 'स्कूप',
    },
    then: 'उसके बाद',
    taperAfter: (n, unit, isFirst) => {
      const units = { day: ['दिन', 'दिनों'], week: ['हफ़्ते', 'हफ़्तों'], month: ['महीने', 'महीनों'], year: ['साल', 'सालों'] }
      return `${isFirst ? 'पहले' : 'अगले'} ${n} ${units[unit][n === 1 ? 0 : 1]} के बाद`
    },
    fullStop: '।',
    duration: { day: (n) => `${n} दिन तक`, week: (n) => `${n} हफ़्ते तक`, month: (n) => `${n} महीने तक`, year: (n) => `${n} साल तक` },
    phrases: {
      'after food': 'खाने के बाद', 'after meals': 'खाने के बाद', 'post meals': 'खाने के बाद',
      'before food': 'खाने से पहले', 'before meals': 'खाने से पहले',
      'with food': 'खाने के साथ', 'along with food': 'खाने के साथ',
      'empty stomach': 'खाली पेट', 'on empty stomach': 'खाली पेट',
      'at bedtime': 'सोते समय', 'before sleeping': 'सोते समय', 'before bed': 'सोते समय', 'hs': 'सोते समय',
      'with milk': 'दूध के साथ', 'with plenty of water': 'खूब पानी के साथ', 'with lots of water': 'खूब पानी के साथ',
      'before breakfast': 'नाश्ते से पहले', 'after breakfast': 'नाश्ते के बाद',
      'after lunch': 'दोपहर के खाने के बाद', 'after dinner': 'रात के खाने के बाद',
      'morning': 'सुबह', 'afternoon': 'दोपहर', 'evening': 'शाम', 'night': 'रात को',
      'once daily': 'दिन में एक बार', 'od': 'दिन में एक बार', 'twice daily': 'दिन में दो बार', 'bd': 'दिन में दो बार', 'bid': 'दिन में दो बार',
      'thrice daily': 'दिन में तीन बार', 'three times daily': 'दिन में तीन बार', 'tds': 'दिन में तीन बार', 'tid': 'दिन में तीन बार',
      'four times daily': 'दिन में चार बार', 'qid': 'दिन में चार बार',
      'weekly': 'हफ़्ते में एक बार', 'once a week': 'हफ़्ते में एक बार', 'alternate days': 'एक दिन छोड़कर',
      'sos': 'ज़रूरत पड़ने पर ही', 'stat': 'तुरंत एक खुराक',
      'continue': 'डॉक्टर की सलाह तक जारी रखें', 'till next visit': 'अगली मुलाक़ात तक', 'until next visit': 'अगली मुलाक़ात तक',
      'lifelong': 'जीवनभर',
    },
  },
  kn: {
    take: (doseList) => `${doseList} ತೆಗೆದುಕೊಳ್ಳಿ`,
    takeBare: (doseText) => `${doseText} ತೆಗೆದುಕೊಳ್ಳಿ`,
    slotPhrase: (qty, slot) => `${slot} ${qty}`,
    slots: { morning: 'ಬೆಳಿಗ್ಗೆ', afternoon: 'ಮಧ್ಯಾಹ್ನ', evening: 'ಸಂಜೆ', night: 'ರಾತ್ರಿ' },
    half: 'ಅರ್ಧ',
    and: 'ಮತ್ತು',
    units: {
      tablet: 'ಮಾತ್ರೆ', tablets: 'ಮಾತ್ರೆ', tab: 'ಮಾತ್ರೆ', tabs: 'ಮಾತ್ರೆ',
      capsule: 'ಕ್ಯಾಪ್ಸೂಲ್', capsules: 'ಕ್ಯಾಪ್ಸೂಲ್', cap: 'ಕ್ಯಾಪ್ಸೂಲ್', caps: 'ಕ್ಯಾಪ್ಸೂಲ್',
      drop: 'ಹನಿ', drops: 'ಹನಿಗಳು', puff: 'ಪಫ್', puffs: 'ಪಫ್',
      teaspoon: 'ಸಣ್ಣ ಚಮಚ', tsp: 'ಸಣ್ಣ ಚಮಚ', tablespoon: 'ದೊಡ್ಡ ಚಮಚ', tbsp: 'ದೊಡ್ಡ ಚಮಚ',
      sachet: 'ಸ್ಯಾಚೆಟ್', scoop: 'ಸ್ಕೂಪ್',
    },
    then: 'ಆ ನಂತರ',
    taperAfter: (n, unit, isFirst) => {
      const units = { day: ['ದಿನದ', 'ದಿನಗಳ'], week: ['ವಾರದ', 'ವಾರಗಳ'], month: ['ತಿಂಗಳ', 'ತಿಂಗಳುಗಳ'], year: ['ವರ್ಷದ', 'ವರ್ಷಗಳ'] }
      return `${isFirst ? 'ಮೊದಲ' : 'ಮುಂದಿನ'} ${n} ${units[unit][n === 1 ? 0 : 1]} ನಂತರ`
    },
    fullStop: '.',
    duration: {
      day: (n) => (n === 1 ? '1 ದಿನದವರೆಗೆ' : `${n} ದಿನಗಳವರೆಗೆ`),
      week: (n) => (n === 1 ? '1 ವಾರದವರೆಗೆ' : `${n} ವಾರಗಳವರೆಗೆ`),
      month: (n) => (n === 1 ? '1 ತಿಂಗಳವರೆಗೆ' : `${n} ತಿಂಗಳುಗಳವರೆಗೆ`),
      year: (n) => (n === 1 ? '1 ವರ್ಷದವರೆಗೆ' : `${n} ವರ್ಷಗಳವರೆಗೆ`),
    },
    phrases: {
      'after food': 'ಊಟದ ನಂತರ', 'after meals': 'ಊಟದ ನಂತರ', 'post meals': 'ಊಟದ ನಂತರ',
      'before food': 'ಊಟಕ್ಕೂ ಮೊದಲು', 'before meals': 'ಊಟಕ್ಕೂ ಮೊದಲು',
      'with food': 'ಊಟದ ಜೊತೆ', 'along with food': 'ಊಟದ ಜೊತೆ',
      'empty stomach': 'ಖಾಲಿ ಹೊಟ್ಟೆಯಲ್ಲಿ', 'on empty stomach': 'ಖಾಲಿ ಹೊಟ್ಟೆಯಲ್ಲಿ',
      'at bedtime': 'ಮಲಗುವ ಮುನ್ನ', 'before sleeping': 'ಮಲಗುವ ಮುನ್ನ', 'before bed': 'ಮಲಗುವ ಮುನ್ನ', 'hs': 'ಮಲಗುವ ಮುನ್ನ',
      'with milk': 'ಹಾಲಿನ ಜೊತೆ', 'with plenty of water': 'ಹೆಚ್ಚು ನೀರಿನ ಜೊತೆ', 'with lots of water': 'ಹೆಚ್ಚು ನೀರಿನ ಜೊತೆ',
      'before breakfast': 'ಬೆಳಗಿನ ತಿಂಡಿಗೂ ಮೊದಲು', 'after breakfast': 'ಬೆಳಗಿನ ತಿಂಡಿಯ ನಂತರ',
      'after lunch': 'ಮಧ್ಯಾಹ್ನದ ಊಟದ ನಂತರ', 'after dinner': 'ರಾತ್ರಿ ಊಟದ ನಂತರ',
      'morning': 'ಬೆಳಿಗ್ಗೆ', 'afternoon': 'ಮಧ್ಯಾಹ್ನ', 'evening': 'ಸಂಜೆ', 'night': 'ರಾತ್ರಿ',
      'once daily': 'ದಿನಕ್ಕೆ ಒಂದು ಬಾರಿ', 'od': 'ದಿನಕ್ಕೆ ಒಂದು ಬಾರಿ', 'twice daily': 'ದಿನಕ್ಕೆ ಎರಡು ಬಾರಿ', 'bd': 'ದಿನಕ್ಕೆ ಎರಡು ಬಾರಿ', 'bid': 'ದಿನಕ್ಕೆ ಎರಡು ಬಾರಿ',
      'thrice daily': 'ದಿನಕ್ಕೆ ಮೂರು ಬಾರಿ', 'three times daily': 'ದಿನಕ್ಕೆ ಮೂರು ಬಾರಿ', 'tds': 'ದಿನಕ್ಕೆ ಮೂರು ಬಾರಿ', 'tid': 'ದಿನಕ್ಕೆ ಮೂರು ಬಾರಿ',
      'four times daily': 'ದಿನಕ್ಕೆ ನಾಲ್ಕು ಬಾರಿ', 'qid': 'ದಿನಕ್ಕೆ ನಾಲ್ಕು ಬಾರಿ',
      'weekly': 'ವಾರಕ್ಕೆ ಒಂದು ಬಾರಿ', 'once a week': 'ವಾರಕ್ಕೆ ಒಂದು ಬಾರಿ', 'alternate days': 'ದಿನ ಬಿಟ್ಟು ದಿನ',
      'sos': 'ಅಗತ್ಯವಿದ್ದಾಗ ಮಾತ್ರ', 'stat': 'ತಕ್ಷಣ ಒಂದು ಡೋಸ್',
      'continue': 'ಸಲಹೆ ಸಿಗುವವರೆಗೆ ಮುಂದುವರಿಸಿ', 'till next visit': 'ಮುಂದಿನ ಭೇಟಿಯವರೆಗೆ', 'until next visit': 'ಮುಂದಿನ ಭೇಟಿಯವರೆಗೆ',
      'lifelong': 'ಜೀವನಪರ್ಯಂತ',
    },
  },
  te: {
    take: (doseList) => `${doseList} తీసుకోండి`,
    takeBare: (doseText) => `${doseText} తీసుకోండి`,
    slotPhrase: (qty, slot) => `${slot} ${qty}`,
    slots: { morning: 'ఉదయం', afternoon: 'మధ్యాహ్నం', evening: 'సాయంత్రం', night: 'రాత్రి' },
    half: 'సగం',
    and: 'మరియు',
    units: {
      tablet: 'మాత్ర', tablets: 'మాత్రలు', tab: 'మాత్ర', tabs: 'మాత్రలు',
      capsule: 'క్యాప్సూల్', capsules: 'క్యాప్సూల్స్', cap: 'క్యాప్సూల్', caps: 'క్యాప్సూల్స్',
      drop: 'చుక్క', drops: 'చుక్కలు', puff: 'పఫ్', puffs: 'పఫ్స్',
      teaspoon: 'చిన్న చెంచా', tsp: 'చిన్న చెంచా', tablespoon: 'పెద్ద చెంచా', tbsp: 'పెద్ద చెంచా',
      sachet: 'శాచెట్', scoop: 'స్కూప్',
    },
    then: 'ఆ తర్వాత',
    taperAfter: (n, unit, isFirst) => {
      const units = { day: ['రోజు', 'రోజుల'], week: ['వారం', 'వారాల'], month: ['నెల', 'నెలల'], year: ['సంవత్సరం', 'సంవత్సరాల'] }
      return `${isFirst ? 'మొదటి' : 'తర్వాతి'} ${n} ${units[unit][n === 1 ? 0 : 1]} తర్వాత`
    },
    fullStop: '.',
    duration: {
      day: (n) => (n === 1 ? '1 రోజు పాటు' : `${n} రోజుల పాటు`),
      week: (n) => (n === 1 ? '1 వారం పాటు' : `${n} వారాల పాటు`),
      month: (n) => (n === 1 ? '1 నెల పాటు' : `${n} నెలల పాటు`),
      year: (n) => (n === 1 ? '1 సంవత్సరం పాటు' : `${n} సంవత్సరాల పాటు`),
    },
    phrases: {
      'after food': 'భోజనం తర్వాత', 'after meals': 'భోజనం తర్వాత', 'post meals': 'భోజనం తర్వాత',
      'before food': 'భోజనానికి ముందు', 'before meals': 'భోజనానికి ముందు',
      'with food': 'భోజనంతో పాటు', 'along with food': 'భోజనంతో పాటు',
      'empty stomach': 'ఖాళీ కడుపుతో', 'on empty stomach': 'ఖాళీ కడుపుతో',
      'at bedtime': 'నిద్రకు ముందు', 'before sleeping': 'నిద్రకు ముందు', 'before bed': 'నిద్రకు ముందు', 'hs': 'నిద్రకు ముందు',
      'with milk': 'పాలతో', 'with plenty of water': 'ఎక్కువ నీటితో', 'with lots of water': 'ఎక్కువ నీటితో',
      'before breakfast': 'అల్పాహారానికి ముందు', 'after breakfast': 'అల్పాహారం తర్వాత',
      'after lunch': 'మధ్యాహ్న భోజనం తర్వాత', 'after dinner': 'రాత్రి భోజనం తర్వాత',
      'morning': 'ఉదయం', 'afternoon': 'మధ్యాహ్నం', 'evening': 'సాయంత్రం', 'night': 'రాత్రి',
      'once daily': 'రోజుకు ఒకసారి', 'od': 'రోజుకు ఒకసారి', 'twice daily': 'రోజుకు రెండుసార్లు', 'bd': 'రోజుకు రెండుసార్లు', 'bid': 'రోజుకు రెండుసార్లు',
      'thrice daily': 'రోజుకు మూడుసార్లు', 'three times daily': 'రోజుకు మూడుసార్లు', 'tds': 'రోజుకు మూడుసార్లు', 'tid': 'రోజుకు మూడుసార్లు',
      'four times daily': 'రోజుకు నాలుగుసార్లు', 'qid': 'రోజుకు నాలుగుసార్లు',
      'weekly': 'వారానికి ఒకసారి', 'once a week': 'వారానికి ఒకసారి', 'alternate days': 'రోజు విడిచి రోజు',
      'sos': 'అవసరమైనప్పుడు మాత్రమే', 'stat': 'వెంటనే ఒక మోతాదు',
      'continue': 'సలహా వచ్చే వరకు కొనసాగించండి', 'till next visit': 'తదుపరి సందర్శన వరకు', 'until next visit': 'తదుపరి సందర్శన వరకు',
      'lifelong': 'జీవితాంతం',
    },
  },
  ta: {
    take: (doseList) => `${doseList} எடுக்கவும்`,
    takeBare: (doseText) => `${doseText} எடுக்கவும்`,
    slotPhrase: (qty, slot) => `${slot} ${qty}`,
    slots: { morning: 'காலை', afternoon: 'மதியம்', evening: 'மாலை', night: 'இரவு' },
    half: 'அரை',
    and: 'மற்றும்',
    units: {
      tablet: 'மாத்திரை', tablets: 'மாத்திரைகள்', tab: 'மாத்திரை', tabs: 'மாத்திரைகள்',
      capsule: 'காப்ஸ்யூல்', capsules: 'காப்ஸ்யூல்கள்', cap: 'காப்ஸ்யூல்', caps: 'காப்ஸ்யூல்கள்',
      drop: 'சொட்டு', drops: 'சொட்டுகள்', puff: 'பஃப்', puffs: 'பஃப்கள்',
      teaspoon: 'சிறிய கரண்டி', tsp: 'சிறிய கரண்டி', tablespoon: 'பெரிய கரண்டி', tbsp: 'பெரிய கரண்டி',
      sachet: 'சாச்செட்', scoop: 'ஸ்கூப்',
    },
    then: 'அதன் பிறகு',
    taperAfter: (n, unit, isFirst) => {
      const units = { day: ['நாளுக்குப்', 'நாட்களுக்குப்'], week: ['வாரத்திற்குப்', 'வாரங்களுக்குப்'], month: ['மாதத்திற்குப்', 'மாதங்களுக்குப்'], year: ['ஆண்டுக்குப்', 'ஆண்டுகளுக்குப்'] }
      return `${isFirst ? 'முதல்' : 'அடுத்த'} ${n} ${units[unit][n === 1 ? 0 : 1]} பிறகு`
    },
    fullStop: '.',
    duration: {
      day: (n) => (n === 1 ? '1 நாளுக்கு' : `${n} நாட்களுக்கு`),
      week: (n) => (n === 1 ? '1 வாரத்திற்கு' : `${n} வாரங்களுக்கு`),
      month: (n) => (n === 1 ? '1 மாதத்திற்கு' : `${n} மாதங்களுக்கு`),
      year: (n) => (n === 1 ? '1 ஆண்டுக்கு' : `${n} ஆண்டுகளுக்கு`),
    },
    phrases: {
      'after food': 'உணவுக்குப் பிறகு', 'after meals': 'உணவுக்குப் பிறகு', 'post meals': 'உணவுக்குப் பிறகு',
      'before food': 'உணவுக்கு முன்', 'before meals': 'உணவுக்கு முன்',
      'with food': 'உணவுடன்', 'along with food': 'உணவுடன்',
      'empty stomach': 'வெறும் வயிற்றில்', 'on empty stomach': 'வெறும் வயிற்றில்',
      'at bedtime': 'தூங்கும் முன்', 'before sleeping': 'தூங்கும் முன்', 'before bed': 'தூங்கும் முன்', 'hs': 'தூங்கும் முன்',
      'with milk': 'பாலுடன்', 'with plenty of water': 'நிறைய தண்ணீருடன்', 'with lots of water': 'நிறைய தண்ணீருடன்',
      'before breakfast': 'காலை உணவுக்கு முன்', 'after breakfast': 'காலை உணவுக்குப் பிறகு',
      'after lunch': 'மதிய உணவுக்குப் பிறகு', 'after dinner': 'இரவு உணவுக்குப் பிறகு',
      'morning': 'காலை', 'afternoon': 'மதியம்', 'evening': 'மாலை', 'night': 'இரவு',
      'once daily': 'நாளுக்கு ஒரு முறை', 'od': 'நாளுக்கு ஒரு முறை', 'twice daily': 'நாளுக்கு இரண்டு முறை', 'bd': 'நாளுக்கு இரண்டு முறை', 'bid': 'நாளுக்கு இரண்டு முறை',
      'thrice daily': 'நாளுக்கு மூன்று முறை', 'three times daily': 'நாளுக்கு மூன்று முறை', 'tds': 'நாளுக்கு மூன்று முறை', 'tid': 'நாளுக்கு மூன்று முறை',
      'four times daily': 'நாளுக்கு நான்கு முறை', 'qid': 'நாளுக்கு நான்கு முறை',
      'weekly': 'வாரத்திற்கு ஒரு முறை', 'once a week': 'வாரத்திற்கு ஒரு முறை', 'alternate days': 'நாள் விட்டு நாள்',
      'sos': 'தேவைப்படும்போது மட்டும்', 'stat': 'உடனே ஒரு டோஸ்',
      'continue': 'ஆலோசனை வரும் வரை தொடரவும்', 'till next visit': 'அடுத்த வருகை வரை', 'until next visit': 'அடுத்த வருகை வரை',
      'lifelong': 'வாழ்நாள் முழுவதும்',
    },
  },
}

const DOSE_TOKEN_REGEX = /^(\d+(?:\.\d+)?|\d+\/\d+|½|¼|¾)$/

function normalizeKey(text) {
  return String(text || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/[.।]+$/, '')
}

function quantityWord(token, lang) {
  if (token === '½' || token === '1/2' || token === '0.5') {
    return lang.half
  }
  return token
}

/**
 * Parses a schedule-pattern dosage ("1-0-1", "1/2 0 1/2", "1-1-1-1") into
 * time-slot quantities. Returns null when the dosage is not a pattern
 * (e.g. "5 ml", "2 puffs") so callers can fall back to plain text.
 */
function parseDosePattern(dosageText) {
  const trimmed = String(dosageText || '').trim().replace(/[‐-―−]/g, '-')
  if (!trimmed) {
    return null
  }
  let tokens = trimmed.split(/\s*-\s*|\s+/).filter(Boolean)
  // compact digit form like "101"
  if (tokens.length === 1 && /^[0-2]{3,4}$/.test(tokens[0]) && tokens[0].includes('0')) {
    tokens = tokens[0].split('')
  }
  if (tokens.length < 2 || tokens.length > 4) {
    return null
  }
  if (!tokens.every((token) => DOSE_TOKEN_REGEX.test(token))) {
    return null
  }
  const slotNamesBySize = {
    2: ['morning', 'night'],
    3: ['morning', 'afternoon', 'night'],
    4: ['morning', 'afternoon', 'evening', 'night'],
  }
  const slotNames = slotNamesBySize[tokens.length]
  const slots = []
  tokens.forEach((token, index) => {
    if (token !== '0' && token !== '0.0') {
      slots.push({ slot: slotNames[index], qty: token })
    }
  })
  return slots.length > 0 ? slots : null
}

function joinWithAnd(parts, lang) {
  if (parts.length <= 1) {
    return parts.join('')
  }
  return `${parts.slice(0, -1).join(', ')} ${lang.and} ${parts[parts.length - 1]}`
}

function buildDurationPhrase(durationText, lang) {
  const trimmed = String(durationText || '').trim()
  if (!trimmed) {
    return ''
  }
  const match = trimmed.match(/^(\d+)\s*(day|days|d|week|weeks|wk|wks|w|month|months|mon|mons|year|years|yr|yrs|y)\.?$/i)
  if (match) {
    const count = parseInt(match[1], 10)
    const unitInitial = match[2][0].toLowerCase()
    const unit = unitInitial === 'd' ? 'day' : unitInitial === 'w' ? 'week' : unitInitial === 'y' ? 'year' : 'month'
    return lang.duration[unit](count)
  }
  const known = lang.phrases[normalizeKey(trimmed)]
  if (known) {
    return known
  }
  // short free text is still worth including as-is; long text is skipped
  return trimmed.length <= 30 ? trimmed : ''
}

function lookupPhrase(text, lang, { includeUnknownShortText = true } = {}) {
  const trimmed = String(text || '').trim()
  if (!trimmed) {
    return ''
  }
  const known = lang.phrases[normalizeKey(trimmed)]
  if (known) {
    return known
  }
  // compound values like "Morning & Night" translate part-by-part
  if (trimmed.includes('&') || trimmed.includes(',')) {
    const parts = trimmed.split(/\s*[,&]\s*/).filter(Boolean)
    const translatedParts = parts.map((part) => lang.phrases[normalizeKey(part)])
    if (translatedParts.length > 1 && translatedParts.every(Boolean)) {
      return joinWithAnd(translatedParts, lang)
    }
  }
  return includeUnknownShortText && trimmed.length <= 30 ? trimmed : ''
}

/** The DB translation stored on a master entry (dose/when/etc.), when present. */
function entryTranslatedTitle(entry, languageCode) {
  const translated = entry?.translations?.[languageCode]?.title
  return translated && String(translated).trim() ? String(translated).trim() : ''
}

/** "1 Tablet" / "2 drops" -> localized unit phrase ("1 गोली"); '' when not a quantity dose. */
function buildQuantityDosePhrase(doseText, lang) {
  if (!lang.units) {
    return ''
  }
  const match = String(doseText || '')
    .trim()
    .match(/^([0-9/.½¼¾]+)\s*([A-Za-z]+)$/)
  if (!match) {
    return ''
  }
  const unitWord = lang.units[match[2].toLowerCase()]
  if (!unitWord) {
    return ''
  }
  const quantity = isHalfQuantity(match[1]) ? lang.half : match[1]
  return `${quantity} ${unitWord}`
}

function isHalfQuantity(token) {
  return token === '½' || token === '1/2' || token === '0.5'
}

const WHAT_TIME_SLOT_TITLES = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
  night: 'Night',
}

/**
 * The time-of-day a pattern dose implies, as a When value ("0-0-1" -> "Night",
 * "1-0-1" -> "Morning & Night"). '' when the dosage is not a schedule pattern —
 * quantity doses ("5 ml") say nothing about timing.
 */
export function getSuggestedWhatTimeForDosage(dosageText) {
  const slots = parseDosePattern(dosageText)
  if (!slots) {
    return ''
  }
  const titles = [...new Set(slots.map(({ slot }) => WHAT_TIME_SLOT_TITLES[slot]))]
  if (titles.length === 0) {
    return ''
  }
  if (titles.length === 1) {
    return titles[0]
  }
  return `${titles.slice(0, -1).join(', ')} & ${titles[titles.length - 1]}`
}

/**
 * The single language the explainer should use, per EMR settings: the first
 * supported non-English preferred prescription language wins, else English.
 */
export function getRxExplainerLanguage(prescriptionViewSettings, prescriptionData) {
  const preferredLanguages =
    prescriptionViewSettings?.preferredPrescriptionLanguages ||
    prescriptionData?.prescriptionLanguages ||
    []
  const nonEnglish = preferredLanguages.find(
    (code) => code !== 'en' && RX_EXPLAINER_SUPPORTED_LANGUAGES.includes(code),
  )
  return nonEnglish || 'en'
}

/**
 * Builds the patient-facing sentence for one Rx row. Returns '' when there is
 * nothing meaningful to say (caller should render nothing).
 */
export function buildRxPatientExplainer({
  dosage,
  whatTime,
  frequency,
  duration,
  notes,
  dosageEntry,
  whatTimeEntry,
  isTaperContinuation = false,
  previousRowDuration,
  isPreviousRowTaperContinuation = false,
  language = 'en',
}) {
  const lang = STRINGS[language] || STRINGS.en

  const sentenceParts = []

  // 1. dose phrase, most specific source first:
  //    pattern parse ("1 in the morning and 1 at night") ->
  //    quantity-dose unit translation ("1 गोली") ->
  //    the dose entry's stored DB translation ->
  //    raw text as last resort
  const doseSlots = parseDosePattern(dosage)
  if (doseSlots) {
    const slotPhrases = doseSlots.map(({ slot, qty }) =>
      lang.slotPhrase(quantityWord(qty, lang), lang.slots[slot]),
    )
    sentenceParts.push(lang.take(joinWithAnd(slotPhrases, lang)))
  } else if (String(dosage || '').trim()) {
    const doseText = String(dosage).trim()
    const localizedDose =
      language === 'en'
        ? ''
        : buildQuantityDosePhrase(doseText, lang) || entryTranslatedTitle(dosageEntry, language)
    if (localizedDose) {
      sentenceParts.push(lang.takeBare(localizedDose))
    } else if (doseText.length <= 30) {
      sentenceParts.push(lang.takeBare(doseText))
    }
    // a pattern dose already encodes the frequency; plain doses get it spelled out
    const frequencyPhrase = lookupPhrase(frequency, lang)
    if (frequencyPhrase) {
      sentenceParts.push(frequencyPhrase)
    }
  } else {
    const frequencyPhrase = lookupPhrase(frequency, lang)
    if (frequencyPhrase) {
      sentenceParts.push(frequencyPhrase)
    }
  }

  // 2. when ("in the morning" / "at bedtime") — skip if already covered by a pattern dose
  if (!doseSlots) {
    const whenPhrase =
      lookupPhrase(whatTime, lang) ||
      (language !== 'en' ? entryTranslatedTitle(whatTimeEntry, language) : '')
    if (whenPhrase) {
      sentenceParts.push(whenPhrase)
    }
  }

  // 3. food/instruction context from notes — only when it maps to a known phrase
  //    (free-text notes already print in the Notes row; repeating them here would shout)
  const notesPhrase = lookupPhrase(notes, lang, { includeUnknownShortText: false })
  if (notesPhrase) {
    sentenceParts.push(notesPhrase)
  }

  // 4. duration ("for 30 days")
  const durationPhrase = buildDurationPhrase(duration, lang)
  if (durationPhrase) {
    sentenceParts.push(durationPhrase)
  }

  if (sentenceParts.length === 0) {
    return ''
  }

  let sentence = sentenceParts.join(', ')
  sentence = sentence.charAt(0).toUpperCase() + sentence.slice(1)
  if (isTaperContinuation) {
    // anchor the taper step to the previous row's duration when it is parseable:
    // "After the first 15 days: ..." (later steps in a chain say "next" instead)
    const prefix =
      buildTaperAfterPrefix(previousRowDuration, !isPreviousRowTaperContinuation, lang) ||
      lang.then
    sentence = `${prefix}: ${sentence.charAt(0).toLowerCase() + sentence.slice(1)}`
  }
  return `${sentence}${lang.fullStop}`
}

function buildTaperAfterPrefix(previousRowDuration, isFirstContinuation, lang) {
  if (!lang.taperAfter) {
    return ''
  }
  const match = String(previousRowDuration || '')
    .trim()
    .match(/^(\d+)\s*(day|days|d|week|weeks|wk|wks|w|month|months|mon|mons|year|years|yr|yrs|y)\.?$/i)
  if (!match) {
    return ''
  }
  const count = parseInt(match[1], 10)
  const unitInitial = match[2][0].toLowerCase()
  const unit =
    unitInitial === 'd' ? 'day' : unitInitial === 'w' ? 'week' : unitInitial === 'y' ? 'year' : 'month'
  return lang.taperAfter(count, unit, isFirstContinuation)
}

/**
 * Convenience wrapper for a drugsAdvice row object (editor state) or a
 * print-converted row.
 */
export function buildRxPatientExplainerForRow(row, language, previousRow) {
  return buildRxPatientExplainer({
    dosage: row?.dosage?.label,
    whatTime: row?.whatTime?.label,
    frequency: row?.frequency?.label,
    duration: row?.duration?.label,
    notes: row?.notes?.label,
    // the selected options carry the master entries (incl. DB translations)
    dosageEntry: row?.dosage,
    whatTimeEntry: row?.whatTime,
    isTaperContinuation: row?.isTaperContinuation === true,
    previousRowDuration: previousRow?.duration?.label,
    isPreviousRowTaperContinuation: previousRow?.isTaperContinuation === true,
    language,
  })
}
